const DASHBOARD_SHAPE_CACHE_VERSION = 3;
const DASHBOARD_SHAPE_CACHE_PREFIX = 'dashboard-services-shape';
const DASHBOARD_SHAPE_CACHE_MAX_AGE_MS = 1000 * 60 * 60 * 24;
const DASHBOARD_SHAPE_CACHE_COMPATIBLE_VERSIONS = new Set([1, 2, 3]);
const DASHBOARD_SHAPE_CACHE_MODE_QUERY_PARAM = 'dashboardCache';
const DASHBOARD_SHAPE_CACHE_MODE_NO_CACHE = 'off';

export const shouldBypassDashboardShapeCache = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    if (!(import.meta.env.DEV || import.meta.env.MODE === 'test')) {
        return false;
    }

    const searchParams = new URLSearchParams(window.location.search);

    return (
        searchParams.get(DASHBOARD_SHAPE_CACHE_MODE_QUERY_PARAM) ===
            DASHBOARD_SHAPE_CACHE_MODE_NO_CACHE ||
        searchParams.get('dashboardNoCache') === '1'
    );
};

const getDashboardShapeCacheKey = (companyID, userEmail) => {
    const normalizedUser = (userEmail || 'anonymous').toLowerCase();

    return `${DASHBOARD_SHAPE_CACHE_PREFIX}:v${DASHBOARD_SHAPE_CACHE_VERSION}:${normalizedUser}:${companyID}`;
};

const getDashboardShapeServiceType = (service) =>
    service?.serviceType ??
    service?.ServiceType ??
    service?.type ??
    service?.Type ??
    null;

const getDashboardShapePlatformId = (service) =>
    service?.platformID ?? service?.platformId ?? service?.PlatformID ?? null;

const getDashboardShapeName = (service) =>
    service?.name ?? service?.Name ?? null;

const getDashboardShapeAppAuthType = (appAuth) =>
    appAuth?.appAuthTypeID ??
    appAuth?.appAuthTypeId ??
    getDashboardShapeServiceType(appAuth);

const createDashboardShapeShell = (items = [], itemType = 'service') => {
    return (items || [])
        .map((item, index) => {
            const serviceType =
                itemType === 'appAuth'
                    ? getDashboardShapeAppAuthType(item)
                    : getDashboardShapeServiceType(item);
            if (!serviceType) return null;

            const platformID = getDashboardShapePlatformId(item);
            const platformToken = platformID || 'none';
            const id = `${itemType}-shape-${serviceType}-${platformToken}-${index}`;

            return {
                id,
                serviceId: id,
                serviceType,
                platformID,
                name: getDashboardShapeName(item),
                appAuthTypeID: itemType === 'appAuth' ? serviceType : undefined,
                __isSkeleton: true,
            };
        })
        .filter(Boolean);
};

export const readDashboardShapeCache = (companyID, userEmail) => {
    if (typeof window === 'undefined' || !companyID) {
        return { services: [], appAuths: [] };
    }

    try {
        const cachedValue = window.localStorage.getItem(
            getDashboardShapeCacheKey(companyID, userEmail),
        );
        if (!cachedValue) return { services: [], appAuths: [] };

        const parsedCache = JSON.parse(cachedValue);
        if (
            !DASHBOARD_SHAPE_CACHE_COMPATIBLE_VERSIONS.has(parsedCache?.version)
        ) {
            return { services: [], appAuths: [] };
        }
        if (
            typeof parsedCache?.updatedAt !== 'number' ||
            Date.now() - parsedCache.updatedAt >
                DASHBOARD_SHAPE_CACHE_MAX_AGE_MS
        ) {
            return { services: [], appAuths: [] };
        }

        const serviceItems = Array.isArray(parsedCache?.services)
            ? parsedCache.services
            : Array.isArray(parsedCache?.items)
              ? parsedCache.items
              : [];
        const appAuthItems = Array.isArray(parsedCache?.appAuths)
            ? parsedCache.appAuths
            : [];

        return {
            services: createDashboardShapeShell(serviceItems),
            appAuths: createDashboardShapeShell(appAuthItems, 'appAuth'),
        };
    } catch (error) {
        return { services: [], appAuths: [] };
    }
};

export const writeDashboardShapeCache = (
    companyID,
    userEmail,
    dashboardShape = {},
) => {
    if (typeof window === 'undefined' || !companyID) return;

    try {
        const cacheKey = getDashboardShapeCacheKey(companyID, userEmail);
        const services = (dashboardShape?.services || [])
            .map((service) => {
                const serviceType = getDashboardShapeServiceType(service);
                if (!serviceType) return null;

                return {
                    serviceType,
                    platformID: getDashboardShapePlatformId(service),
                    name: getDashboardShapeName(service),
                };
            })
            .filter(Boolean);
        const appAuths = (dashboardShape?.appAuths || [])
            .map((appAuth) => {
                const appAuthTypeID = getDashboardShapeAppAuthType(appAuth);
                if (!appAuthTypeID) return null;

                return {
                    appAuthTypeID,
                    platformID: getDashboardShapePlatformId(appAuth),
                    name: getDashboardShapeName(appAuth),
                };
            })
            .filter(Boolean);

        if (!services.length && !appAuths.length) {
            window.localStorage.removeItem(cacheKey);
            return;
        }

        window.localStorage.setItem(
            cacheKey,
            JSON.stringify({
                version: DASHBOARD_SHAPE_CACHE_VERSION,
                updatedAt: Date.now(),
                services,
                appAuths,
            }),
        );
    } catch (error) {
        // Ignore local storage errors and fall back to the generic loader.
    }
};
